import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
	Container,
	ContentContainer,
	TopLine,
	TopLineSection,
	TopLineItem,
	TopLineItemHover,
	IdText,
	DateText,
	NameText,
	ShadowBox,
	ContentLineSection,
	ImageSection,
	LabelsSection,
	DeadlineSection,
	BottomLine,
	DetailsSection,
	DetailElement,
	ProgressBar,
	GroupedItems,
	BottomLineDetailElement,
	ParentTaskNameSection,
	SubTaskIconItem,
	ClientSection,
} from './card.styled';
import {TopBarAndChip} from './top_bar_and_chip';
import {SimpleTooltip} from '../../util-components/simple-tooltip/simple_tooltip';
import {PriorityIcon} from '../../icons/priority-icon/priority_icon';
import {BugIcon} from '../../icons/bug-icon/bug_icon';
import {DependencyIcon} from '../../icons/dependency-icon/dependency_icon';
import {BlockedIcon} from '../../icons/blocked-icon/blocked_icon';
import {VerticalDotsIcon} from '../../icons/vertical-dots-icon';
import {SubtaskIcon} from '../../icons/subtask-icon';
import {SubTaskIcon} from '../../design-icons/SubTaskIcon';
import {AttachmentIcon} from '../../icons/attachment-icon';
import {CommentIcon} from '../../icons/comment-icon';
import {Checkbox} from '../../util-components/checkbox';
import {AssigneeGroup} from '../../core/assignee-group';
import {SIZE} from '../../design-icons/base-icon';
export const Card = ({
	theEyeOptions,
	id,
	name,
	isSelected,
	isFavoured,
	cardDisabled,
	starIcon,
	projectIndicatorIcon,
	showProjectIndicator,
	highPriority,
	isBug,
	hasDependencies,
	showBlocked,
	imageComponent,
	deadline,
	labels,
	onCardClick,
	todosSubtasksCount,
	attachmentsCount,
	commentsCount,
	onContextMenu,
	contextMenuElement,
	showCheckbox,
	onCheckboxClick,
	progress,
	assignees,
	showContext,
	showTimeReg,
	timeRegPopupElement,
	intl,
	showProgressBar,
	cy,
	parentTaskName,
	clientName,
}) => {
	const getId = id => 'T' + id;
	const handleCardClick = () => {
		onCardClick(id);
	};

	const handleCheckboxClick = e => {
		e.stopPropagation();
		onCheckboxClick && onCheckboxClick(e);
	};

	const getDeadlineFormat = date => {
		const daysFromNow = date.startOf('day').diff(moment().startOf('day'), 'days');
		switch (daysFromNow) {
			case -1:
				return intl.formatMessage({id: 'common.yesterday'});
			case 0:
				return intl.formatMessage({id: 'common.today'});
			case 1:
				return intl.formatMessage({id: 'common.tomorrow'});
			default:
				return date.format('DD MMM');
		}
	};

	return (
		<>
			<Container cardDisabled={cardDisabled} onClick={handleCardClick} data-cy={cy}>
				{theEyeOptions.progress && showProgressBar ? (
					<ProgressBar isSelected={isSelected} progress={progress}>
						<div className="progress-bar-container">
							<div className="progress-bar" />
						</div>
					</ProgressBar>
				) : null}
				<ShadowBox className="shadow-box" cardDisabled={cardDisabled} isSelected={isSelected} isFavoured={isFavoured} />
				<TopBarAndChip cardDisabled={cardDisabled} isSelected={isSelected} isFavoured={isFavoured} />
				<ContentContainer onContextMenu={onContextMenu}>
					<TopLine>
						<TopLineSection>
							{showCheckbox ? (
								<TopLineItem>
									<Checkbox
										cy="selector-checkbox"
										onClick={handleCheckboxClick}
										checked={isSelected}
										userpilot={'bulk-update-checkbox'}
									/>
								</TopLineItem>
							) : null}
							{showProjectIndicator && (
								<TopLineItem data-cy="card-project-indicator">{projectIndicatorIcon()}</TopLineItem>
							)}
							{theEyeOptions.highPriority && highPriority ? (
								<TopLineItem>
									<SimpleTooltip>
										<PriorityIcon
											active
											size={PriorityIcon.SIZE.SMALL}
											shouldShow={theEyeOptions.highPriority && highPriority}
											intl={intl}
										/>
									</SimpleTooltip>
								</TopLineItem>
							) : null}
							{theEyeOptions.bug && isBug ? (
								<TopLineItem>
									<SimpleTooltip>
										<BugIcon
											active
											size={BugIcon.SIZE.SMALL}
											color={'#535353'}
											shouldShow={theEyeOptions.bug && isBug}
											intl={intl}
										/>
									</SimpleTooltip>
								</TopLineItem>
							) : null}
							{theEyeOptions.dependencies && hasDependencies ? (
								<TopLineItem>
									<SimpleTooltip>
										<DependencyIcon active size={DependencyIcon.SIZE.SMALL} color={'#535353'} />
									</SimpleTooltip>
								</TopLineItem>
							) : null}
							{showBlocked ? (
								<TopLineItem>
									<SimpleTooltip>
										<BlockedIcon
											active
											size={BlockedIcon.SIZE.SMALL}
											shouldShow={showBlocked}
											intl={intl}
										/>
									</SimpleTooltip>
								</TopLineItem>
							) : null}
						</TopLineSection>
						<TopLineSection>
							<TopLineItem last>
								<SimpleTooltip>{starIcon({isFavoured: isFavoured})}</SimpleTooltip>
							</TopLineItem>
							{showContext ? (
								<TopLineItemHover last>
									<VerticalDotsIcon
										onClick={onContextMenu}
										size={VerticalDotsIcon.SIZE.MEDIUM}
										color={'#545454'}
										cy="card-context-menu"
									/>
								</TopLineItemHover>
							) : null}
						</TopLineSection>
					</TopLine>
					{parentTaskName ? (
						<ParentTaskNameSection>
							<p>{parentTaskName}</p>
						</ParentTaskNameSection>
					) : null}
					<ContentLineSection>
						{parentTaskName ? (
							<SubTaskIconItem>
								<SubTaskIcon size={SIZE.SMALL} />
							</SubTaskIconItem>
						) : null}
						<NameText parentTask={parentTaskName}>
							<span data-cy="card-task-name">{name}</span>
							{theEyeOptions.taskId ? <IdText>{`#${getId(id)}`}</IdText> : null}
						</NameText>
					</ContentLineSection>
					{theEyeOptions.dates && deadline ? (
						<DeadlineSection>
							<p>{intl.formatMessage({id: 'common.deadline_colon'})} </p>
							<DateText>{getDeadlineFormat(deadline)}</DateText>
						</DeadlineSection>
					) : null}
					{theEyeOptions.clientName && clientName ? (
						<ClientSection>
							<p>{intl.formatMessage({id: 'common.client'}) + ':'}</p>
							<DateText>{clientName}</DateText>
						</ClientSection>
					) : null}
					{theEyeOptions.imagePreview && imageComponent ? <ImageSection>{imageComponent}</ImageSection> : null}
					{theEyeOptions.labels && labels ? <LabelsSection>{labels()}</LabelsSection> : null}
					<BottomLine>
						<DetailsSection>
							{theEyeOptions.todosSubtasks && todosSubtasksCount > 0 ? (
								<DetailElement>
									<SubtaskIcon size={SubtaskIcon.SIZE.SMALL} />
									{todosSubtasksCount}
								</DetailElement>
							) : null}
							{theEyeOptions.attachments && attachmentsCount > 0 ? (
								<DetailElement>
									<AttachmentIcon size={AttachmentIcon.SIZE.SMALL}></AttachmentIcon>
									{attachmentsCount}
								</DetailElement>
							) : null}
							{theEyeOptions.comments && commentsCount > 0 ? (
								<DetailElement>
									<CommentIcon size={CommentIcon.SIZE.SMALL}></CommentIcon>
									{commentsCount}
								</DetailElement>
							) : null}
						</DetailsSection>

						<GroupedItems>
							<BottomLineDetailElement>
								{showTimeReg && timeRegPopupElement ? timeRegPopupElement() : null}
							</BottomLineDetailElement>
							{theEyeOptions.assigned && assignees.length > 0 ? (
								<AssigneeGroup showCount={3} assignees={assignees} />
							) : null}
						</GroupedItems>
					</BottomLine>
				</ContentContainer>
			</Container>
			{showContext && contextMenuElement ? contextMenuElement() : null}
		</>
	);
};

Card.propTypes = {
	intl: PropTypes.shape({
		formatMessage: PropTypes.func,
	}),
	/**
	 * enable context
	 */
	showContext: PropTypes.bool,
	/**
	 * Elements to show (or not) in the row
	 */
	theEyeOptions: PropTypes.shape({
		checkbox: PropTypes.bool,
		projectId: PropTypes.bool,
		highPriority: PropTypes.bool,
		bug: PropTypes.bool,
		dependencies: PropTypes.bool,
		taskId: PropTypes.bool,
		clientName: PropTypes.bool,
		imagePreview: PropTypes.bool,
		dates: PropTypes.bool,
		labels: PropTypes.bool,
		taskDetails: PropTypes.bool,
		todosSubtasks: PropTypes.bool,
		attachments: PropTypes.bool,
		comments: PropTypes.bool,

		assigned: PropTypes.bool,
		owner: PropTypes.bool,
		status: PropTypes.bool,
		progress: PropTypes.bool,
		estimate: PropTypes.bool,
		timeEntries: PropTypes.bool,
		remaining: PropTypes.bool,
		star: PropTypes.bool,
	}),
	assignees: PropTypes.arrayOf(
		PropTypes.shape({
			fullName: PropTypes.string.isRequired,
			imageSource: PropTypes.string,
		})
	),
	/**
	 * Task id
	 */
	id: PropTypes.number.isRequired,
	showTimeReg: PropTypes.bool,
	cardDisabled: PropTypes.bool,
	timeRegPopupElement: PropTypes.func,
	/**
	 * percentage of task done
	 */
	progress: PropTypes.number,
	/**
	 * Task name
	 */
	name: PropTypes.string.isRequired,
	/**
	 * toggles selected visual on card
	 */
	isSelected: PropTypes.bool,
	/**
	 * toggles star icon
	 */
	isFavoured: PropTypes.bool,

	/**
	 * The star icon from web-components, optionally wrapped in another component
	 */
	starIcon: PropTypes.func.isRequired,

	/**
	 * Project IndicatorIcon from web-components, optionally wrapped in another component
	 */
	projectIndicatorIcon: PropTypes.func.isRequired,
	/**
	 * Should the project indicator be shown?
	 */
	showProjectIndicator: PropTypes.bool.isRequired,
	/**
	 * Project id of the tast
	 */
	projectId: PropTypes.number,

	/**
	 * Project color for coloring the project indicator icon
	 */
	projectColor: PropTypes.string,

	/**
	 * high priority icon on card
	 */
	highPriority: PropTypes.bool,
	/**
	 * Task is a bug
	 */
	isBug: PropTypes.bool,
	/**
	 * Toggles dependency icon
	 */
	hasDependencies: PropTypes.bool,
	/**
	 * Toggles blocked icon
	 */
	showBlocked: PropTypes.bool,

	/**
	 * Cover image of the task
	 */
	coverImage: PropTypes.func,

	/**
	 * Component displaying the image if more advanced features are needed
	 */
	imageComponent: PropTypes.element,

	/**
	 * function to be called on card click
	 */
	onCardClick: PropTypes.func,
	showProgressBar: PropTypes.bool,

	/**
	 * deadline of the task
	 */
	deadline: PropTypes.object,

	/**
	 * Labels of the task
	 */
	labels: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),

	/**
	 * Number of Todos or subtasks of the task
	 */
	todosSubtasksCount: PropTypes.number,

	/**
	 * Number of documents attached to the task
	 */
	attachmentsCount: PropTypes.number,

	/**
	 * Number of comments on the task
	 */
	commentsCount: PropTypes.number,

	/**
	 * Function to open the context menu
	 */
	onContextMenu: PropTypes.func,

	/**
	 * Context menu
	 */
	contextMenuElement: PropTypes.func,

	/**
	 * Cypress information
	 */
	cy: PropTypes.string,
	showCheckbox: PropTypes.bool,
	onCheckboxClick: PropTypes.func,
	/**
	 * Name of the immediate parent task name
	 */
	parentTaskName: PropTypes.string,

	/**
	 * Name of the client
	 */
	clientName: PropTypes.string,
};

Card.defaultProps = {
	progress: 0,
	intl: {
		formatMessage: ({id}) => id,
	},

	assignees: [],
	theEyeOptions: {
		checkbox: true,
		projectId: true,
		highPriority: true,
		bug: true,
		dependencies: true,
		taskId: true,
		imagePreview: true,
		dates: true,
		labels: true,
		taskDetails: true,
		todosSubtasks: true,
		attachments: true,
		comments: true,

		assigned: true,
		owner: true,
		status: true,
		progress: true,
		estimate: true,
		timeEntries: true,
		remaining: true,
		star: true,
	},
	onCardClick: () => false,
};
